/**
 * Converter for a table (docs/18-vorratsbuch.md, Eingänge, E-3): Excel or CSV, a row per good, into
 * the same Eingangsbeleg as a delivery note. Matching, storage, best-before and booking are then the
 * Eingang's, as for a PDF. What the table says about a new article beyond its quantity (brand, base
 * product, container, restock rule, barcode) travels in `fields` of the line.
 *
 * Rules:
 *   - A row needs a name and a quantity; a row without one of them is left out with a note.
 *   - The unit is a base unit (g, kg, ml, l, Stk.) or a container (Dose, Packung …). A container
 *     with „Inhalt je Gebinde“ counts its content: 3 Dosen à 400 g are 1200 g.
 *   - The same table twice is recognised by its content, so nothing is booked twice.
 */
import type { Cell } from "./catalog-import.ts";
import { CONTAINERS } from "./drinks.ts";
import { contentOf, inboundUnit, quantityOf } from "./inbound.ts";
import type { BaseUnit, Inbound, InboundLine } from "./inbound.ts";
import { guessBaseProduct } from "./inbound-match.ts";
import { DRINKS_AISLE, baseProduct, searchBaseProducts } from "./base-products.ts";
import { lookup } from "@household-kit/food";
import { germanDate, germanNumber } from "./pdf-lines.ts";
import { fold } from "./search.ts";

export interface TableColumn {
  key: string;
  label: string;
  /** Other headings that mean this column, folded. */
  aliases: readonly string[];
  hint: string;
  example: readonly (string | number | null)[];
}

/** The columns of the template, in this order; * marks the required ones. */
export const TABLE_COLUMNS: readonly TableColumn[] = [
  { key: "product", label: "Bezeichnung", aliases: ["name", "artikel", "produkt", "ware", "bezeichnung"], hint: "Pflicht. Wie auf der Packung: „Passierte Tomaten“, „Spülmaschinentabs“", example: ["Passata", "Weizenmehl 405", "Eier Freiland"] },
  { key: "brand", label: "Marke", aliases: ["marke", "hersteller", "brand"], hint: "leer, wenn egal", example: ["Mutti", null, null] },
  { key: "base", label: "Grundprodukt", aliases: ["grundprodukt", "produktart", "kategorie"], hint: "wie auf dem Einkaufszettel; leer: VorratsBuch schlägt eins vor", example: ["Passierte Tomaten", "Mehl", "Eier"] },
  { key: "qty", label: "Menge", aliases: ["menge", "anzahl", "stueck", "stuck"], hint: "Pflicht. Wie viele der Einheit daneben: 3 (Dosen), 500 (g), 1,5 (kg)", example: [3, 2, 10] },
  { key: "unit", label: "Einheit", aliases: ["einheit", "unit", "me"], hint: "g, kg, ml, l, Stk. oder ein Gebinde: Dose, Packung, Glas, Flasche …; leer: Stk.", example: ["Flasche", "Packung", "Stk."] },
  { key: "size", label: "Inhalt je Gebinde", aliases: ["inhalt", "inhaltjegebinde", "groesse", "packungsgroesse", "fuellmenge"], hint: "bei einem Gebinde: 700 (g), 1000 (ml), 40 (Tabs)", example: [700, 1000, null] },
  { key: "base_unit", label: "Zählen in", aliases: ["zaehlenin", "grundeinheit", "inhaltin"], hint: "g, ml oder Stk.: worin der Inhalt angegeben ist; leer: g", example: ["g", "g", "Stk."] },
  { key: "bestBefore", label: "MHD", aliases: ["mhd", "haltbarbis", "mindesthaltbarkeit", "mindesthaltbarbis", "ablaufdatum"], hint: "Datum, z. B. 31.12.2026", example: ["31.12.2027", "30.06.2027", null] },
  { key: "place", label: "Lager", aliases: ["lager", "lagerort", "ort", "wo"], hint: "Name des Lagers: „Vorratsschrank“, „Kühlschrank“; leer: wie oben im Eingang gewählt", example: ["Vorratsschrank", "Vorratsschrank", "Kühlschrank"] },
  { key: "amount", label: "Preis gesamt (€)", aliases: ["preis", "preisgesamt", "betrag", "gesamt", "gesamtpreis", "euro"], hint: "für die ganze Zeile, nicht je Stück", example: [5.97, 1.58, 3.49] },
  { key: "barcode", label: "Strichcode", aliases: ["strichcode", "ean", "gtin", "barcode"], hint: "die Ziffern unter dem Strichcode", example: [null, null, null] },
  { key: "organic", label: "Bio", aliases: ["bio", "organic"], hint: "ja oder leer", example: ["ja", null, "ja"] },
  { key: "reorderAt", label: "Nachkaufen ab", aliases: ["nachkaufenab", "meldebestand", "mindestbestand"], hint: "so viele Gebinde oder weniger: auf die Nachkaufliste", example: [1, 1, null] },
  { key: "fillTo", label: "Auffüllen auf", aliases: ["auffuellenauf", "zielbestand"], hint: "so viele Gebinde sollen da sein", example: [4, 2, null] },
];

const key = (s: string) => fold(s).replace(/ /g, "");

/** Which column a heading stands for, or null. */
export function tableColumnOf(heading: string): string | null {
  const h = key(heading.replace(/\*/g, ""));
  if (h === "") return null;
  for (const c of TABLE_COLUMNS) if (key(c.label) === h || c.aliases.includes(h)) return c.key;
  // „Preis in €“, „Menge (Stück)“: a known word at the start.
  for (const c of TABLE_COLUMNS) if (c.aliases.some((a) => a.length >= 4 && h.startsWith(a))) return c.key;
  return null;
}

const text = (c: Cell): string => (c === null || c === undefined ? "" : c instanceof Date ? "" : String(c).trim());
const num = (c: Cell): number | null => (typeof c === "number" ? c : germanNumber(text(c)));
const yes = (c: Cell) => c === true || /^(ja|j|x|yes|1|true|bio)$/i.test(text(c));

function isoDay(c: Cell): string | undefined {
  if (c instanceof Date) return `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}-${String(c.getDate()).padStart(2, "0")}`;
  const t = text(c);
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  return germanDate(t) ?? undefined;
}

/** A base product named in the table: exactly by one of its names, else the best hit of the search. */
/** A drink by its name („Apfelsaft“): KellerLog's, not taken in here (F18; B16). */
const isDrinkName = (name: string) => name !== "" && lookup(name)?.product.aisle === DRINKS_AISLE;

function namedBase(name: string) {
  if (name === "") return undefined;
  return guessBaseProduct(name) ?? searchBaseProducts(name, 1)[0];
}

/** A small, stable fingerprint of the goods, for the key against booking the same table twice. */
function fingerprint(lines: readonly InboundLine[]): string {
  const s = JSON.stringify(lines.map((l) => [l.text, l.qty, l.unit, l.amount ?? null, l.bestBefore ?? null, l.place ?? null]));
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/** The Eingangsbeleg of a table: the first row with „Bezeichnung“ and „Menge“ is the heading. */
export function parseInboundTable(rows: readonly Cell[][]): Inbound {
  const notes: string[] = [];
  const head = rows.findIndex((r) => {
    const keys = r.map((c) => tableColumnOf(text(c)));
    return keys.includes("product") && keys.includes("qty");
  });
  if (head < 0) {
    return { channel: "table", format: "table", kind: "delivery", key: "table:?", lines: [], notes: ["Keine Spalten „Bezeichnung“ und „Menge“ gefunden. Die Vorlage zeigt, wie die Tabelle aussieht."] };
  }
  const cols = new Map<string, number>();
  rows[head]!.forEach((c, i) => {
    const k = tableColumnOf(text(c));
    if (k && !cols.has(k)) cols.set(k, i);
  });
  const at = (r: Cell[], k: string): Cell => (cols.has(k) ? r[cols.get(k)!] : null);

  const lines: InboundLine[] = [];
  rows.slice(head + 1).forEach((r, i) => {
    const row = head + i + 2;
    const product = text(at(r, "product"));
    const qtyCell = at(r, "qty");
    if (!product && (qtyCell === null || qtyCell === undefined || text(qtyCell) === "")) return;
    const qty = num(qtyCell);
    if (!product) return void notes.push(`Zeile ${row}: Bezeichnung fehlt, ausgelassen.`);
    if (qty === null || qty <= 0) return void notes.push(`Zeile ${row} („${product}“): Menge fehlt, ausgelassen.`);

    const brand = text(at(r, "brand"));
    const written = text(at(r, "unit"));
    const unit = written || "Stk.";
    const u = inboundUnit(unit);
    const container = Object.keys(CONTAINERS).find((c) => key(c) === key(unit));
    if (u === null && !container) notes.push(`Zeile ${row} („${product}“): Einheit „${unit}“ unbekannt, als Stück gezählt.`);
    const size = num(at(r, "size"));
    const baseUnit = text(at(r, "base_unit"));
    const contentUnit: BaseUnit = baseUnit === "ml" || baseUnit === "l" ? "ml" : /^st/i.test(baseUnit) ? "Stk." : "g";
    const factor = baseUnit === "kg" || baseUnit === "l" ? 1000 : 1;

    let q = quantityOf(product, qty, u === null ? "Stk." : container ? "Packung" : unit);
    if (size !== null && size > 0 && (container || u === "pack")) {
      const content = { qty: Math.round(size * factor), unit: contentUnit };
      q = { name: contentOf(product).name, packs: qty, content, total: { qty: Math.round(qty * content.qty), unit: content.unit } };
    }
    const base = namedBase(text(at(r, "base")));
    // A drink goes to KellerLog, as from the shopping list: the line stays out (B16).
    const drink = !base && (text(at(r, "base")) ? isDrinkName(text(at(r, "base"))) : !guessBaseProduct(product) && isDrinkName(product));
    if (drink) notes.push(`Zeile ${row} („${product}“): Getränke gehören in KellerLog; nicht eingelagert.`);
    else if (text(at(r, "base")) && !base) notes.push(`Zeile ${row} („${product}“): Grundprodukt „${text(at(r, "base"))}“ nicht im Katalog.`);
    const price = num(at(r, "amount"));
    const barcode = text(at(r, "barcode")).replace(/\s/g, "");
    const bestBefore = isoDay(at(r, "bestBefore"));
    const place = text(at(r, "place"));
    const reorderAt = num(at(r, "reorderAt"));
    const fillTo = num(at(r, "fillTo"));
    const fields: Record<string, unknown> = {
      ...(brand ? { brand } : {}),
      ...(base ? { base: base.id } : {}),
      ...(container ? { container } : {}),
      ...(barcode ? { barcodes: [barcode] } : {}),
      ...(reorderAt !== null ? { reorderAt } : {}),
      ...(fillTo !== null ? { fillTo } : {}),
    };
    lines.push({
      line: lines.length + 1,
      text: brand ? `${brand} ${product}` : product,
      ...q,
      qty,
      unit: written || "Stk.",
      ...(price !== null ? { amount: Math.round(price * 100) } : {}),
      ...(yes(at(r, "organic")) ? { organic: true } : {}),
      ...(barcode ? { barcode } : {}),
      ...(bestBefore ? { bestBefore } : {}),
      ...(place ? { place } : {}),
      ...(drink ? { group: "Getränke" } : {}),
      ...(Object.keys(fields).length ? { fields } : {}),
    });
  });
  if (lines.length === 0 && notes.length === 0) notes.push("In der Tabelle stehen keine Waren.");
  return { channel: "table", format: "table", kind: "delivery", key: `table:${fingerprint(lines)}`, lines, notes };
}

/** The template: heading, three examples. */
export function inboundTemplate(): (string | number | null)[][] {
  return [TABLE_COLUMNS.map((c) => c.label), ...[0, 1, 2].map((i) => TABLE_COLUMNS.map((c) => c.example[i] ?? null))];
}

/** The second sheet of the template: what each column means. */
export function inboundGuide(): string[][] {
  return [["Spalte", "Was hinein gehört"], ...TABLE_COLUMNS.map((c) => [c.label, c.hint])];
}

/** The base product a table names for a line, for the matching (`fields.base`). */
export const tableBase = (l: InboundLine) => (typeof l.fields?.base === "string" ? baseProduct(l.fields.base) : undefined);
